import * as React from 'react'
import { useState, useCallback, useEffect } from 'react'
import { jt, t } from 'ttag'
import { Card } from '../../components/Card/Card'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import YumButton from '../../components/YumButton/YumButton'
import YumVerificationCodeInput from '../../components/YumVerificationCodeInput/YumVerificationCodeInput'
import Typography from '@material-ui/core/Typography'
import { secondaryText } from '../../styles/material-ui-theme-v2'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import { ReactComponent as ArrowBackIcon } from '../../assets/v3/icons/arrow-back.svg'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'
import { announce } from '../../components/a11y/useAnnouncer'
import './VerifyEmail.scss'

type Purpose = 'LOGIN' | 'SIGNUP' | 'PASSWORD_RESET'

export interface IVerifyEmailStateProps {
  loading: boolean
  resending: boolean
  error: string | null
  purpose: Purpose
  email: string
}

export interface IVerifyEmailDispatchProps {
  verifyEmailCode: (payload: { code: string; purpose: Purpose }) => void
  resendVerificationCode: (payload: { purpose: Purpose }) => void
  doLogOut: () => void
}

type VerifyEmailProps = IVerifyEmailStateProps & IVerifyEmailDispatchProps

const CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

const VerifyEmail: React.FC<VerifyEmailProps> = ({
  loading,
  resending,
  error,
  purpose,
  email,
  verifyEmailCode,
  resendVerificationCode,
  doLogOut,
}) => {
  const [code, setCode] = useState('')
  // The back-end has just emailed a code, so start the cooldown immediately on mount.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS)
  const [codeError, setCodeError] = useState(false)
  // Assertive status for resend actions, rendered into a role="alert" region
  // below (WCAG 4.1.3). The imperative announce() helper is unreliable here:
  // its singleton node carries role="status" (implicitly polite), and the
  // polite update fires just as the Resend button is removed from the DOM and
  // focus drops to the body, so screen readers often drop it. A declarative
  // role="alert" region is reliably announced after the user activates Resend.
  const [resendStatus, setResendStatus] = useState('')

  // The previous page navigated here without a full page load, so announce
  // what happened for screen reader users (WCAG 4.1.3).
  useEffect(() => {
    announce(t`We've sent a 6-digit verification code to your email.`)
  }, [])

  // Decrement the cooldown once per second until it reaches 0. The countdown is
  // deliberately NOT in a live region -- announcing every second would drown out
  // everything else; resend availability is announced once below instead.
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown((prev) => Math.max(0, prev - 1)), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  // Announce once when the resend option becomes available. Routed through the
  // role="alert" region (not announce()) so it is reliably spoken; the
  // per-second countdown is still NOT announced -- only this state change is.
  const cooldownActive = cooldown > 0
  useEffect(() => {
    if (!cooldownActive) {
      setResendStatus(t`You can now request a new verification code.`)
    }
  }, [cooldownActive])

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event) event.preventDefault()
      if (code.length !== CODE_LENGTH) {
        setCodeError(true)
        // Focus the first empty digit box (attribute selectors don't see the
        // React-controlled value, so check the property).
        const inputs = Array.from(document.querySelectorAll('.verify-email__form input')) as HTMLInputElement[]
        const firstEmpty = inputs.find((input) => !input.value)
        ;(firstEmpty || inputs[0])?.focus()
        return
      }
      setCodeError(false)
      verifyEmailCode({ code, purpose })
    },
    [code, purpose, verifyEmailCode],
  )

  const handleResend = useCallback(() => {
    if (cooldown > 0 || resending) return
    resendVerificationCode({ purpose })
    setCooldown(RESEND_COOLDOWN_SECONDS)
    // Confirm the resend and that the countdown restarted, so screen reader users
    // know the request went through (WCAG 4.1.3).
    setResendStatus(
      t`A new verification code has been sent to your email. You can request another code in ${RESEND_COOLDOWN_SECONDS} seconds.`,
    )
  }, [cooldown, resending, purpose, resendVerificationCode])

  return (
    <Card className="verify-email pt-12">
      <div className="verify-email__inner">
        <button
          type="button"
          className="yum-back"
          style={{ cursor: 'pointer', border: 'none', background: 'none' }}
          onClick={() => doLogOut()}
          title={t`Back`}
          aria-label={t`Back`}
        >
          <ArrowBackIcon aria-hidden="true" />
        </button>
        <NotificationsWrapper />
        <form className="verify-email__form" onSubmit={handleSubmit} noValidate>
          <Typography className="h1-desktop" variant="h1" style={{ textAlign: 'center' }}>
            {t`Two-factor authentication`}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 16, marginBottom: 8, opacity: 0.7, textAlign: 'center' }}>
            {email
              ? jt`We've sent a 6-digit verification code to: ${(<strong key="email">{email}</strong>)}`
              : t`We've sent a 6-digit verification code to your email.`}
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 16, color: secondaryText, textAlign: 'center' }}>
            {t`If you can't find the email, please check your spam folder.`}
          </Typography>
          <YumVerificationCodeInput
            value={code}
            onChange={(next) => {
              setCode(next)
              if (codeError) setCodeError(false)
            }}
            onComplete={(next) => verifyEmailCode({ code: next, purpose })}
            error={!!error || codeError}
            disabled={loading}
            autoFocus={true}
            length={CODE_LENGTH}
          />
          {codeError && <ErrorMessage>{t`Please enter the 6-digit verification code.`}</ErrorMessage>}
          {error && (
            <ErrorMessage>
              {error === 'expiredCode'
                ? t`The code has expired. Please request a new one.`
                : error === 'tooManyAttempts'
                  ? t`Too many failed attempts. Please request a new code.`
                  : t`The code you entered is incorrect. Please try again.`}
            </ErrorMessage>
          )}
          <YumButton type="submit" showLoader={loading} disabled={loading} style={{ marginTop: 16 }}>
            {purpose === 'LOGIN' ? t`Log in` : t`Verify`}
          </YumButton>
          <div className="text-center" style={{ marginTop: 16, fontSize: '0.875rem' }}>
            {/* #8E8E93 on white is ~3.4:1; #495881 is ~5.4:1 and satisfies WCAG 1.4.3. */}
            <span style={{ color: '#495881' }}>{t`Didn't receive code?`}</span>{' '}
            {cooldown > 0 ? (
              <span style={{ color: '#495881' }}>{t`Resend in ${cooldown}s`}</span>
            ) : (
              <button
                type="button"
                className="link-blue"
                style={{ cursor: resending ? 'default' : 'pointer' }}
                onClick={handleResend}
              >
                {t`Resend`}
              </button>
            )}
          </div>
          {/* Assertive status for resend actions (WCAG 4.1.3). Visually hidden;
              the per-second countdown above is intentionally not announced. */}
          <div
            role="alert"
            aria-live="assertive"
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {resendStatus}
          </div>
        </form>
      </div>
      <DesktopSidebar />
    </Card>
  )
}

export default VerifyEmail
